'use client';

import React, { useEffect, useRef, useState } from 'react';

function useInView(threshold = 0.1) {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          observer.disconnect();
        }
      },
      { threshold }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [threshold]);

  return { ref, inView };
}

interface Service {
  icon: React.ReactNode;
  title: string;
  description: string;
  features: string[];
  color: string;
  bgColor: string;
}

const services: Service[] = [
  {
    icon: (
      <svg className="w-7 h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
      </svg>
    ),
    title: 'Business Consulting',
    description: 'Strategic guidance to help organizations navigate complex challenges, identify growth opportunities, and achieve sustainable competitive advantage.',
    features: ['Strategic Planning', 'Market Entry Strategy', 'Competitive Analysis', 'Growth Roadmaps'],
    color: '#4284F5',
    bgColor: 'rgba(66, 132, 245, 0.1)',
  },
  {
    icon: (
      <svg className="w-7 h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
      </svg>
    ),
    title: 'Technology Consulting',
    description: 'Digital transformation and technology strategy services to modernize operations, enhance capabilities, and drive innovation across your enterprise.',
    features: ['Digital Transformation', 'IT Strategy', 'Systems Architecture', 'Cloud Advisory'],
    color: '#00BDA5',
    bgColor: 'rgba(0, 189, 165, 0.1)',
  },
  {
    icon: (
      <svg className="w-7 h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
      </svg>
    ),
    title: 'Management Services',
    description: 'Operational improvement and organizational efficiency consulting to streamline processes, optimize resources, and build high-performance teams.',
    features: ['Process Optimization', 'Org Design', 'Change Management', 'Performance Management'],
    color: '#8B5CF6',
    bgColor: 'rgba(139, 92, 246, 0.1)',
  },
  {
    icon: (
      <svg className="w-7 h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
      </svg>
    ),
    title: 'Research & Analysis',
    description: 'Data-driven insights and comprehensive market research to inform strategic decisions, validate assumptions, and uncover hidden opportunities.',
    features: ['Market Research', 'Competitive Intelligence', 'Data Analytics', 'Feasibility Studies'],
    color: '#F59E0B',
    bgColor: 'rgba(245, 158, 11, 0.1)',
  },
  {
    icon: (
      <svg className="w-7 h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
      </svg>
    ),
    title: 'Engineering Services',
    description: 'Technical solutions and engineering expertise to solve complex operational challenges, improve systems reliability, and drive technical innovation.',
    features: ['Systems Engineering', 'Process Engineering', 'Technical Assessments', 'Quality Assurance'],
    color: '#EF4444',
    bgColor: 'rgba(239, 68, 68, 0.1)',
  },
  {
    icon: (
      <svg className="w-7 h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
    title: 'Accounting & Financial Advisory',
    description: 'Financial strategy and compliance guidance to optimize financial performance, ensure regulatory adherence, and support informed business decisions.',
    features: ['Financial Strategy', 'Compliance Advisory', 'Budget Optimization', 'Risk Management'],
    color: '#10B981',
    bgColor: 'rgba(16, 185, 129, 0.1)',
  },
];

export default function ServicesSection() {
  const { ref, inView } = useInView();
  const [activeService, setActiveService] = useState<number | null>(null);

  return (
    <section
      id="services"
      className="py-24"
      style={{ background: '#FFFFFF' }}
    >
      <div className="max-w-7xl mx-auto px-6">
        {/* Section header */}
        <div
          ref={ref}
          className={`text-center mb-16 transition-all duration-700 ${
            inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <div
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-semibold mb-4"
            style={{
              background: 'rgba(66, 132, 245, 0.1)',
              border: '1px solid rgba(66, 132, 245, 0.2)',
              color: '#4284F5',
            }}
          >
            Our Services
          </div>
          <h2
            className="font-bold text-navy-dark mb-4"
            style={{ fontSize: 'clamp(28px, 4vw, 44px)' }}
          >
            Comprehensive Consulting
            <span
              className="block"
              style={{
                background: 'linear-gradient(135deg, #4284F5, #21427B)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
              }}
            >
              Solutions for Every Challenge
            </span>
          </h2>
          <p className="text-gray-500 text-lg max-w-2xl mx-auto">
            From strategic planning to technical implementation, we deliver end-to-end consulting
            services tailored to your organization's unique needs.
          </p>
        </div>

        {/* Services grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((service, i) => (
            <div
              key={i}
              className={`p-7 rounded-card cursor-pointer transition-all duration-300 group ${
                inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
              style={{
                background: activeService === i ? service.bgColor : 'white',
                border: `1px solid ${activeService === i ? service.color + '40' : 'rgba(66, 132, 245, 0.1)'}`,
                boxShadow: activeService === i
                  ? `0 8px 40px ${service.color}20`
                  : '0 2px 16px rgba(66, 132, 245, 0.06)',
                transitionDelay: `${i * 60}ms`,
                transform: activeService === i ? 'translateY(-4px)' : 'translateY(0)',
              }}
              onMouseEnter={() => setActiveService(i)}
              onMouseLeave={() => setActiveService(null)}
            >
              {/* Icon */}
              <div
                className="w-14 h-14 rounded-xl flex items-center justify-center mb-5 transition-all duration-300"
                style={{
                  background: activeService === i ? service.color : service.bgColor,
                  color: activeService === i ? 'white' : service.color,
                }}
              >
                {service.icon}
              </div>

              {/* Title */}
              <h3 className="font-bold text-navy-dark text-lg mb-3">{service.title}</h3>

              {/* Description */}
              <p className="text-gray-500 text-sm leading-relaxed mb-5">{service.description}</p>

              {/* Features */}
              <div className="flex flex-col gap-2">
                {service.features.map((feature, j) => (
                  <div key={j} className="flex items-center gap-2">
                    <div
                      className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                      style={{ background: service.color }}
                    />
                    <span className="text-gray-600 text-xs">{feature}</span>
                  </div>
                ))}
              </div>

              {/* Arrow */}
              <div
                className={`mt-5 flex items-center gap-2 text-sm font-semibold transition-all duration-300 ${
                  activeService === i ? 'opacity-100 translate-x-1' : 'opacity-0 translate-x-0'
                }`}
                style={{ color: service.color }}
              >
                Learn More
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div
          className={`text-center mt-14 transition-all duration-700 delay-500 ${
            inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <p className="text-gray-500 mb-6">Not sure which service fits your needs?</p>
          <button
            onClick={() => {
              const el = document.getElementById('contact');
              if (el) el.scrollIntoView({ behavior: 'smooth' });
            }}
            className="px-8 py-4 text-white font-semibold rounded-btn shadow-btn transition-all duration-200 hover:opacity-90 hover:-translate-y-0.5"
            style={{ background: 'linear-gradient(291deg, #4284F5 0%, #21427B 100%)' }}
          >
            Schedule a Free Consultation
          </button>
        </div>
      </div>
    </section>
  );
}
